import type { StateStorage } from "zustand/middleware";
import { idbKvDel, idbKvGet, idbKvSet } from "./indexedDbStringKv";

/**
 * IndexedDB-backed storage for Zustand `persist` (desktop web). Much larger quota
 * than localStorage. On first read, migrates a legacy localStorage value for the
 * same key into IDB and removes it from localStorage.
 */
export function createZustandIdbStateStorage(): StateStorage {
  return {
    getItem: async (name) => {
      try {
        const fromIdb = await idbKvGet(name);
        if (fromIdb != null) return fromIdb;
      } catch {
        /* IDB read failed — use legacy only below */
      }
      if (typeof localStorage === "undefined") return null;
      try {
        const legacy = localStorage.getItem(name);
        if (legacy == null) return null;
        try {
          await idbKvSet(name, legacy);
          localStorage.removeItem(name);
        } catch {
          /* IDB broken — keep reading from localStorage until a write succeeds */
        }
        return legacy;
      } catch {
        return null;
      }
    },
    setItem: async (name, value) => {
      try {
        await idbKvSet(name, value);
        try {
          if (typeof localStorage !== "undefined") {
            localStorage.removeItem(name);
          }
        } catch {
          /* ignore */
        }
        return;
      } catch {
        /* IDB write failed — best-effort legacy */
      }
      try {
        if (typeof localStorage !== "undefined") {
          localStorage.setItem(name, value);
        }
      } catch {
        /* QuotaExceededError or disabled */
      }
    },
    removeItem: async (name) => {
      try {
        await idbKvDel(name);
      } catch {
        /* ignore */
      }
      try {
        if (typeof localStorage !== "undefined") {
          localStorage.removeItem(name);
        }
      } catch {
        /* ignore */
      }
    },
  };
}
